import axios from 'axios';
import { API_URL } from '../config';
import moment from 'moment';
import { AxiosRequestConfig } from 'axios';

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  permission?: string;
}

export const exportEmployeeList = async (
  token: string,
  file_format: string,
  filters: string[],
  filterBy: string,
  startDate?: string,
  endDate?: string
) => {
  const formatted = file_format.toLowerCase();

  const params: any = {
    file_format: formatted,
    "filters[]": filters,
    filter_by: filterBy,
  };
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;

  const res = await axios.get(`${API_URL}/presenze/company/export-employees/`, {
    headers: { Authorization: token },
    params,
  });

  return { status: res.status, data: res.data };
};


export const exportEmployeeAttendance = async (
  token: string,
  startDate: string,
  endDate: string,
  format: string,
  selectedUnit: number | null,
  employeeIds: number[] = []
) => {
  const params = {
    start_date: startDate,
    end_date: endDate,
    request_format: format,
    unit: selectedUnit,
    "employees[]": employeeIds,
  };

  const response = await axios.get(`${API_URL}/presenze/company/export-presenze/`, {
    headers: { Authorization: token },
    params,
  });

  // attesi: {download_id, detail, status?}
  return { status: response.status, data: response.data };
};


export const exportCostRefunds = async (token: string, month: number, year: number) => {
  const response = await axios.get(`${API_URL}/presenze/company/export-cost-refund/`, {
    headers: { Authorization: token },
    params: { month, year },
  });
  return { status: response.status, data: response.data };
};


export const exportAttendanceReports = async (
  token: string,
  startDate: string,
  endDate: string,
  selectedUnit: number | null
) => {
  const params = { start_date: startDate, end_date: endDate, ou: selectedUnit };

  const res = await axios.get(`${API_URL}/presenze/company/reports/attendance/export/`, {
    headers: { Authorization: token },
    params,
  });

  return { status: res.status, data: res.data };
};


export const exportAttendanceAnalyticReports = async (
  token: string,
  startDate: string,
  endDate: string,
  selectedUnit: number | null
) => {
  const params = { start_date: startDate, end_date: endDate, ou: selectedUnit };

  const res = await axios.get(`${API_URL}/presenze/company/reports/attendance-analytic/export/`, {
    headers: { Authorization: token },
    params,
  });

  return { status: res.status, data: res.data };
};


export const exportAttendanceWorkingAreaReports = async (
  token: string,
  startDate: string,
  endDate: string,
  selectedWorkingArea: number | null
) => {
  const params = { start_date: startDate, end_date: endDate, working_area: selectedWorkingArea };

  const res = await axios.get(`${API_URL}/presenze/company/reports/attendance-working-areas/export/`, {
    headers: {
      'Authorization': token,
    },
    params,
  });

  return { status: res.status, data: res.data };
};


export const exportAttendanceWorkingAreaCsvReports = async (
  token: string,
  startDate: string,
  endDate: string,
  selectedWorkingArea: number | null
) => {
  const params = {
    start_date: startDate,
    end_date: endDate,
    working_area: selectedWorkingArea
  };

  const res = await axios.get(
    `${API_URL}/presenze/company/reports/attendance-analytic-working-areas/export/`,
    {
      headers: {
        'Authorization': token,
      },
      params,
    }
  );

  return { status: res.status, data: res.data };
};


export const ExportInouts = async (
  token: string,
  startDate: string,
  endDate: string,
  employeeIds: number[],
  unitId: number | "",
  includeInouts?: boolean,
  includeShifts?: boolean,
  includeJustifications?: boolean
): Promise<{
  status: number;
  data: { detail: string; download_id: number; status?: string };
}> => {

  const params = new URLSearchParams();

  params.set("start_date", startDate);
  params.set("end_date", endDate);

  employeeIds.forEach((id) => params.append("employees[]", String(id)));

  if (unitId) params.set("unit", String(unitId));

  params.set("include_inouts", String(!!includeInouts));
  params.set("include_shifts", String(!!includeShifts));
  params.set("include_justifications", String(!!includeJustifications));

  const res = await axios.get(
    `${API_URL}/presenze/company/export-inouts/?${params.toString()}`,
    {
      headers: {
        Authorization: `${token}`,
      },
    }
  );

  return {
    status: res.status,
    data: res.data,
  };
};

export const ExportDeadlines = async (
  token: string,
  startDate: string,
  endDate: string,
  includeDocuments?: boolean,
  includeContracts?: boolean,
  ouId?: number | string | null,
  includeSignedDocuments?: boolean,
): Promise<{
  status: number;
  data: { detail: string; download_id: number; status?: string };
}> => {

  const params = new URLSearchParams();

  params.set("start_date", startDate);
  params.set("end_date", endDate);

  params.set("include_documents", String(!!includeDocuments));
  params.set("include_contracts", String(!!includeContracts));
  params.set("include_signed_documents", String(!!includeSignedDocuments));

  if (ouId != null && ouId !== "") {
    params.set("ou", String(ouId));
  }

  const res = await axios.get(
    `${API_URL}/presenze/company/export-deadlines/?${params.toString()}`,
    {
      headers: {
        Authorization: `${token}`,
      },
    }
  );

  return {
    status: res.status,
    data: res.data,
  };
};

export const fetchFileDownloadList = async (
  token: string,
  page: number = 1,
  q: string = "",
  createdAt: string = "",
  permission: string | null
): Promise<{
  count: number;
  next: string | null;
  previous: string | null;
  results: {
    id: number;
    file_name: string | null;
    created_at: string;
    expired_at: string | null;
    status: string;
    status_label: string;
    download_url: string | null;
    requested_by: string;
  }[];
}> => {
  const response = await axios.get(`${API_URL}/presenze/company/download_list/`,
    {
      headers: {
        Authorization: `${token}`,
      },
      params: { page, q: q || undefined, created_at: createdAt || undefined },
      permission,
    } as CustomAxiosRequestConfig);

  return response.data 
};

export async function downloadFile(token: string, url: string, fallbackName = "download.xlsx") {
  const res = await axios.get(url, {
    headers: { Authorization: `${token}` },
    responseType: "blob",
  });

  // prova a leggere il filename da Content-Disposition
  const cd = res.headers["content-disposition"] || "";
  const match = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
  const filename = match ? decodeURIComponent(match[1]) : fallbackName;

  const blobUrl = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(blobUrl);
}
